import { useState, useEffect, useCallback, ReactNode, useMemo } from 'react'
import { Currency, currencySymbol, Product } from '../../utils/payment-utils'
import ProductOptions from './product-options'
import ModalOverlay, {
  ModalOverlayContent,
} from '../ui/shared/overlays/modal-overlay'
import { logException, logMessage } from '../../logger'
import LoadingDots from '../ui/shared/loading-dots'
import ValidationMessage from '../ui/shared/validation-message'
import { PaymentMethod, PaymentIntent, Stripe } from '@stripe/stripe-js'
import useIsMounted from '../../hooks/use-is-mounted'
import CardComponent from './card'
import PayButtons, { PayingState } from './pay-buttons'
import { LoadingState } from '../../types/common'
import Select from '../ui/elements/select'

type SubscriptionOptions = {
  subscription: {
    name: string
    // eslint-disable-next-line camelcase
    payg_options?: { credit: number }
  }
  currency: Currency
}
type PurchaseResult = {
  status: PaymentIntent.Status
  paymentIntentClientSecret: string
  lastPaymentError: PaymentIntent.LastPaymentError
  hostedInvoiceUrl: string
}

interface PaymentModalProps {
  show: boolean
  title: string
  instructions: ReactNode
  onDismiss: () => void
  onPaymentSuccess?: () => void
  products: Product[]
  defaultProductId?: string
  currency?: Currency
  createSubscription: (
    subscriptionOptions: SubscriptionOptions
  ) => Promise<PurchaseResult>
  getPurchaseUrl: (
    productId: string,
    currency: string,
    successUrl: string,
    cancelUrl: string
  ) => string
  getUserPaymentMethod: () => Promise<PaymentMethod>
  getStripe: () => Promise<Stripe | null>
}

export default function PaymentsModal({
  show,
  title,
  instructions,
  currency,
  onDismiss,
  onPaymentSuccess,
  products,
  defaultProductId,
  createSubscription,
  getPurchaseUrl,
  getUserPaymentMethod,
  getStripe,
}: PaymentModalProps): JSX.Element {
  const [selectedProduct, setSelectedProduct] = useState<string | undefined>(
    defaultProductId
  )
  const [selectedCurrency, setSelectedCurrency] = useState(
    currency || Currency.eur
  )
  const [payingState, setPayingState] = useState<PayingState>('idle')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>()
  const [loading, setLoading] = useState<LoadingState>('idle')
  const [allOptionsDisabled, setAllOptionsDisabled] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const defaultErrorMessage = 'Something went wrong'

  const validationMessageType =
    payingState === 'error'
      ? 'danger'
      : payingState === 'success'
      ? 'success'
      : 'default'

  const isMounted = useIsMounted()

  useEffect(() => {
    if (show) {
      setLoading('loading')
      getUserPaymentMethod()
        .then(paymentMethod => {
          if (isMounted.current) {
            setPaymentMethod(paymentMethod)
            setLoading('done')
          }
        })
        .catch(error => {
          // 404 is a normal response when payment method is missing
          if (error.response?.status !== 404) {
            logMessage(error)
          }
          setLoading('done')
        })
    }
  }, [show, getUserPaymentMethod, isMounted])

  useEffect(() => {
    if (payingState === 'success' && onPaymentSuccess) {
      onPaymentSuccess()
    }
  }, [payingState, onPaymentSuccess])

  const internalOnDismiss = useCallback(() => {
    if (payingState !== 'buying') {
      onDismiss()
    }
  }, [payingState, onDismiss])

  const checkoutUrl = useMemo(() => {
    if (selectedProduct && currency) {
      const returnUrl = new URL(window.location.href)
      const cancelUrl = returnUrl.toString()
      returnUrl.searchParams.append('wait_for', selectedProduct)
      const successUrl = returnUrl.toString()
      return getPurchaseUrl(selectedProduct, currency, successUrl, cancelUrl)
    } else {
      return null
    }
  }, [currency, getPurchaseUrl, selectedProduct])

  return (
    <ModalOverlay title={title} onDismiss={internalOnDismiss} show={show}>
      <ModalOverlayContent>
        {loading === 'loading' ? (
          <LoadingDots primaryColor />
        ) : loading === 'done' ? (
          <>
            {instructions}
            {!currency ? (
              <div className="tw-my-3">
                <h4 className="tw-custom-reset-heading tw-mb-3">Currency</h4>
                <Select
                  value={selectedCurrency}
                  onChange={e =>
                    setSelectedCurrency(e.target.value as Currency)
                  }>
                  {Object.keys(Currency).map(currency => (
                    <option key={currency} value={currency}>
                      {currency.toUpperCase()}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
            <ProductOptions
              products={products.map(product => ({
                ...product,
                disabled: allOptionsDisabled,
              }))}
              currency={selectedCurrency}
              onChange={setSelectedProduct}
              selectedId={selectedProduct}
            />
            {paymentMethod && paymentMethod.card && (
              <>
                <CardComponent card={paymentMethod.card} />
                {checkoutUrl ? (
                  <div className="tw-mt-2">
                    <a
                      href={checkoutUrl}
                      className="tw-text-sm tw-text-primary hover:tw-underline hover:tw-text-primary-hover">
                      Pay with a different card
                    </a>
                  </div>
                ) : null}
              </>
            )}
            <ValidationMessage type={validationMessageType}>
              {payingState === 'success' && (
                <>
                  <span className="tw-mr-2">
                    <i className="fa fa-check"></i>
                  </span>
                  <span>Payment successful</span>
                </>
              )}
              {payingState === 'error' && errorMessage}
            </ValidationMessage>
            {selectedCurrency ? (
              <PayButtons
                onPay={
                  paymentMethod
                    ? () => onBuyCredits(selectedCurrency)
                    : () => redirectToCheckout(selectedCurrency)
                }
                payDisabled={payingState === 'buying' || !selectedProduct}
                payingState={payingState}
                payLabel={
                  paymentMethod
                    ? `Pay ${
                        selectedProduct
                          ? `${currencySymbol[selectedCurrency]}${
                              products.find(({ id }) => id === selectedProduct)
                                ?.prices[selectedCurrency]
                            }`
                          : ''
                      }`
                    : 'Proceed to checkout'
                }
                onDismiss={internalOnDismiss}
              />
            ) : null}
          </>
        ) : null}
      </ModalOverlayContent>
    </ModalOverlay>
  )

  function onReturnPaymentIntent(
    status: PaymentIntent.Status,
    paymentIntentClientSecret: string,
    lastPaymentError: PaymentIntent.LastPaymentError,
    hostedInvoiceUrl: string
  ) {
    lastPaymentError?.message
      ? setErrorMessage(lastPaymentError.message)
      : setErrorMessage(undefined)
    switch (status) {
      case 'succeeded':
        setPayingState('success')
        break
      case 'requires_action':
        confirmPaymentIntent(paymentIntentClientSecret, hostedInvoiceUrl)
        break
      default:
        window.location.href = hostedInvoiceUrl
        break
    }
  }

  async function confirmPaymentIntent(
    paymentIntentClientSecret: string,
    hostedInvoiceUrl: string
  ) {
    const stripe = await getStripe()
    if (stripe) {
      try {
        const result = await stripe.confirmCardPayment(
          paymentIntentClientSecret,
          {
            payment_method: paymentMethod?.id,
          }
        )
        result.error?.message
          ? setErrorMessage(result.error?.message)
          : setErrorMessage(undefined)
        if (result.paymentIntent?.status === 'succeeded') {
          setPayingState('success')
        } else {
          window.location.href = hostedInvoiceUrl
        }
      } catch (error) {
        logException(error)
        setPayingState('error')
        setErrorMessage(defaultErrorMessage)
        setAllOptionsDisabled(false)
      }
    } else {
      logException('Could not initialize stripe')
      setPayingState('error')
      setErrorMessage(defaultErrorMessage)
      setAllOptionsDisabled(false)
    }
  }

  async function onBuyCredits(currency: string) {
    setPayingState('buying')
    setAllOptionsDisabled(true)

    const product = products.find(({ id }) => id === selectedProduct)

    if (!product) {
      throw new Error(`Unknown product ${selectedProduct} selected.`)
    }

    const [category] = product.id.split('-')

    const subscription =
      category === 'payg'
        ? {
            name: 'payg',
            payg_options: { credit: product.prices[currency] },
          }
        : { name: product.id }

    try {
      const {
        status,
        paymentIntentClientSecret,
        lastPaymentError,
        hostedInvoiceUrl,
      } = await createSubscription({
        subscription,
        currency: selectedCurrency,
      })
      onReturnPaymentIntent(
        status,
        paymentIntentClientSecret,
        lastPaymentError,
        hostedInvoiceUrl
      )
    } catch (error) {
      logException(error)
      setPayingState('error')
      setErrorMessage(defaultErrorMessage)
      setAllOptionsDisabled(false)
    }
  }

  function redirectToCheckout(currency: string) {
    setAllOptionsDisabled(true)

    if (selectedProduct) {
      const returnUrl = window.location.href
      const checkoutUrl = getPurchaseUrl(
        selectedProduct,
        currency,
        returnUrl,
        returnUrl
      )
      window.location.href = checkoutUrl
    }
  }
}
